import { AuthContext } from '@core/shared';
import { AccountManagerInvite, AccountManagerInviteStatus, AccountManagerRights, OptionsApi } from '@core/swagger-client';
import { Button, Delete, ErrorMessagePage, Heading, Input, LoaderPage, ProfileAbbreviation, User } from '@core/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { PageNotFound, useValidations } from '../../..';

export const ManagedAccount = () => {
    const { t } = useTranslation();
    const { hasRights, rights } = useContext(AuthContext);
    if (!hasRights) {
        return <PageNotFound />;
    }

    const {
        register,
        formState: { errors },
        handleSubmit,
        resetField
    } = useForm<AccountManagerInvite>({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        defaultValues: {
            rights,
            emailAddress: '',
            status: AccountManagerInviteStatus.Pending
        }
    });
    const onSubmit: SubmitHandler<AccountManagerInvite> = (data) => {
        inviteAccountManager(data);
    };
    const { emailValidation } = useValidations();

    const accountManagers = useQuery(OptionsApi.accountManagement.getAccountManagers());
    const accountManagerInvites = useQuery(OptionsApi.accountManagement.getAccountManagerInvites());

    const updateAccountManagerMutation = useMutation(OptionsApi.accountManagement.updateAccountManager());
    const updateAccountManager = (manager: AccountManagerRights) => {
        updateAccountManagerMutation.mutate(manager, { onSuccess: () => accountManagers.refetch() });
    };
    const removeAccountManagerMutation = useMutation(OptionsApi.accountManagement.removeAccountManager());
    const removeAccountManager = (managerId: string) => {
        removeAccountManagerMutation.mutate(managerId, { onSuccess: () => accountManagers.refetch() });
    };

    const inviteAccountManagerMutation = useMutation(OptionsApi.accountManagement.inviteAccountManager());
    const inviteAccountManager = (invite: AccountManagerInvite) => {
        inviteAccountManagerMutation.mutate(invite, { onSuccess: () => accountManagerInvites.refetch() });
        resetField('emailAddress');
    };
    const removeAccountManagerInviteMutation = useMutation(OptionsApi.accountManagement.removeAccountManagerInvite());
    const removeAccountManagerInvite = (emailAddress: string) => {
        removeAccountManagerInviteMutation.mutate(emailAddress, { onSuccess: () => accountManagerInvites.refetch() });
    };

    const error = accountManagers.error ?? accountManagerInvites.error;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (accountManagers.isLoading || accountManagerInvites.isLoading) {
        return <LoaderPage />;
    }

    return (
        <>
            <div className="p-4 md:p-8">
                <Heading variant="h2" classNames="pb-4 md:pb-10">
                    {t('Account.Management.PageHeading')}
                </Heading>
                <div className="mb-10">
                    <Trans i18nKey="Account.Management.InfoText" components={{ ul: <ul className="list-disc my-2 ps-7" />, li: <li />, em: <em /> }} />
                </div>
                <form className="max-w-lg mb-10" onSubmit={handleSubmit(onSubmit)}>
                    <Input label={t('Account.Label.Email')} {...register('emailAddress', emailValidation)} errorMessage={errors.emailAddress?.message}></Input>
                    <Button isLoading={inviteAccountManagerMutation.isPending} variant="primary">
                        {t('Account.Management.Button.Invite')}
                    </Button>
                </form>
                <Heading variant="h3" classNames="pb-4 md:pb-6">
                    {t('Account.Management.Heading.InvitedAccounts')}
                </Heading>
                <div className="flex flex-col gap-4">
                    {accountManagerInvites.data &&
                        accountManagerInvites.data.map((invite) => (
                            <div className="flex items-center border border-gray-200 rounded-button overflow-hidden" key={invite.emailAddress}>
                                <div
                                    className="flex-none size-12 menu:size-14 md:size-16 bg-account rounded-r-button grid place-items-center text-xl text-white uppercase"
                                    aria-hidden="true"
                                >
                                    <User classNames="size-8 stroke-white" />
                                </div>
                                <span className="flex-1 ms-4 me-3 truncate">{invite.emailAddress}</span>
                                {invite.status === AccountManagerInviteStatus.Pending && (
                                    <span className="flex-none bg-accent rounded-full text-xs menu:text-sm menu:me-2 px-2 py-1">
                                        {t('Account.Management.InviteStatus.Pending')}
                                    </span>
                                )}
                                {invite.status === AccountManagerInviteStatus.Rejected && (
                                    <span className="flex-none bg-warning rounded-full text-white text-xs menu:text-sm menu:me-2 px-2 py-1">
                                        {t('Account.Management.InviteStatus.Rejected')}
                                    </span>
                                )}
                                <Button
                                    classNames="flex-none"
                                    iconbtn
                                    onClick={(_) => removeAccountManagerInvite(invite.emailAddress)}
                                    isLoading={removeAccountManagerInviteMutation.isPending}
                                >
                                    <Delete classNames="size-6 flex-none stroke-warning" />
                                    <span className="sr-only">
                                        <Trans i18nKey="Account.Management.Button.RemoveAccount" values={{ account: invite.emailAddress }} />
                                    </span>
                                </Button>
                            </div>
                        ))}
                    {accountManagers.data &&
                        accountManagers.data.map((manager) => (
                            <div className="flex items-center border border-gray-200 rounded-button overflow-hidden" key={manager.account.accountId}>
                                <div
                                    className="flex-none size-12 menu:size-14 md:size-16 bg-account rounded-r-button grid place-items-center text-xl text-white uppercase overflow-hidden"
                                    aria-hidden="true"
                                >
                                    {!!manager.account.assets?.profileImage?.imageUrl && (
                                        <img src={manager.account.assets.profileImage.imageUrl} className="w-full h-full object-cover" alt="" />
                                    )}
                                    {!manager.account.assets?.profileImage?.imageUrl && <ProfileAbbreviation profile={manager.account.profile} />}
                                </div>
                                <span className="flex-1 ms-4 me-3 truncate">{manager.account.profile.emailAddress}</span>
                                <span className="flex-none bg-success rounded-full text-xs menu:text-sm menu:me-2 px-2 py-1">
                                    {t('Account.Management.InviteStatus.Accepted')}
                                </span>
                                <Button
                                    classNames="flex-none"
                                    iconbtn
                                    onClick={(_) => removeAccountManager(manager.rights.accountId)}
                                    isLoading={removeAccountManagerMutation.isPending}
                                >
                                    <Delete classNames="size-6 flex-none stroke-warning" />
                                    <span className="sr-only">
                                        <Trans i18nKey="Account.Management.Button.RemoveAccount" values={{ account: manager.account.profile.emailAddress }} />
                                    </span>
                                </Button>
                            </div>
                        ))}
                </div>
            </div>
        </>
    );
};
